import type { TenantSettings } from '@lastrouter/contracts';
import { Alert, Badge, Button, Card, CardContent, CardHeader, CardTitle, checkAccent, Field, Input, isHexColor, Textarea } from '@lastrouter/ui';
import { CheckCircle2, ShieldCheck } from '@lastrouter/ui';
import { useEffect, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client.js';
import { useApiQuery } from '../api/hooks.js';
import { AdminOnly } from '../app/shell.js';
import { PageHeader, QueryState } from '../components/common.js';
import { formatNumber } from '../lib/format.js';
import { useSaveSettings } from './settings-save.js';

const MAX_IMAGE = 200 * 1024;
const TYPES = ['image/png', 'image/jpeg', 'image/webp'];

/** Lê a imagem como data URL depois de conferir tipo e tamanho. */
export function readImage(file: File): Promise<{ ok: true; url: string } | { ok: false; reason: 'tipo' | 'tamanho' }> {
  if (!TYPES.includes(file.type)) return Promise.resolve({ ok: false, reason: 'tipo' });
  if (file.size > MAX_IMAGE) return Promise.resolve({ ok: false, reason: 'tamanho' });
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve({ ok: true, url: String(r.result) });
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

function ImageField({ id, label, value, onChange }: { id: string; label: string; value: string | null; onChange: (v: string | null) => void }) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | undefined>();
  return (
    <div className="flex flex-col gap-2">
      <Field id={id} label={label} hint={t('appearance.imageHelp')} error={error}>
        <Input
          type="file"
          accept={TYPES.join(',')}
          className="py-1.5"
          onChange={async (e) => {
            const f = e.target.files?.[0];
            if (!f) return;
            const r = await readImage(f);
            if (r.ok) {
              setError(undefined);
              onChange(r.url);
            } else setError(r.reason === 'tipo' ? t('appearance.wrongType') : t('appearance.tooLarge'));
          }}
        />
      </Field>
      {value ? (
        <div className="flex items-center gap-3">
          <img src={value} alt="" className="h-10 w-auto rounded-[var(--lr-radius-sm)] border border-border bg-surface p-1" />
          <Button size="sm" variant="ghost" onClick={() => onChange(null)}>
            {t('appearance.removeImage')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function Preview({ name, accent, icon }: { name: string; accent: string; icon: string | null }) {
  const { t } = useTranslation();
  // A prévia usa a cor escolhida mesmo antes de salvar, só dentro deste cartão.
  const style = isHexColor(accent) ? ({ '--accent': accent } as CSSProperties) : undefined;
  return (
    <Card style={style}>
      <CardHeader>
        <CardTitle>{t('appearance.preview')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <p className="self-end rounded-[var(--lr-radius-lg)] bg-sunken px-3 py-2">{t('appearance.previewQuestion')}</p>
        <div className="flex gap-2">
          {icon ? <img src={icon} alt="" className="size-8 rounded-full" /> : <span aria-hidden="true" className="size-8 shrink-0 rounded-full bg-accent" />}
          <div className="flex flex-col gap-2 rounded-[var(--lr-radius-lg)] border border-border p-3">
            <span className="text-[length:var(--lr-text-sm)] font-semibold">{name || 'Lastrouter'}</span>
            <span className="text-[length:var(--lr-text-lg)] font-semibold">{t('appearance.previewAnswer')}</span>
            <Badge tone="success" className="self-start">
              <ShieldCheck aria-hidden="true" />
              {t('appearance.certified')}
            </Badge>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="primary">
                {t('appearance.useful')}
              </Button>
              <Button size="sm">{t('appearance.wrong')}</Button>
              <Button size="sm">{t('appearance.askReview')}</Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function AppearanceForm({ settings }: { settings: TenantSettings }) {
  const { t } = useTranslation();
  const { save, pending } = useSaveSettings('appearance.saved', ['publicAppearance']);
  const [a, setA] = useState(settings.appearance);
  const [serverProblems, setServerProblems] = useState<string[] | null>(null);
  useEffect(() => setA(settings.appearance), [settings]);

  const hexOk = isHexColor(a.accent_color);
  const check = hexOk ? checkAccent(a.accent_color) : null;

  // Confere também no serviço (a regra que vale ao salvar), meio segundo depois de parar de digitar.
  useEffect(() => {
    if (!hexOk) return;
    setServerProblems(null);
    const timer = setTimeout(() => {
      api('checkAppearance', { body: { accent_color: a.accent_color } })
        .then((r) => setServerProblems(r.problems))
        .catch(() => setServerProblems(null));
    }, 500);
    return () => clearTimeout(timer);
  }, [a.accent_color, hexOk]);

  const ok = Boolean(check?.ok) && a.assistant_name.trim().length > 0;
  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_420px]">
      <Card>
        <CardContent className="flex flex-col gap-5">
          <Field id="nome-assistente" label={t('appearance.name')} error={a.assistant_name.trim() ? undefined : t('common.required')}>
            <Input value={a.assistant_name} maxLength={60} onChange={(e) => setA({ ...a, assistant_name: e.target.value })} />
          </Field>
          <div className="flex flex-col gap-2">
            <Field id="cor-destaque" label={t('appearance.color')} hint={t('appearance.colorHelp')} error={hexOk ? undefined : t('appearance.invalidColor')}>
              <Input value={a.accent_color} maxLength={7} className="max-w-40 font-mono" onChange={(e) => setA({ ...a, accent_color: e.target.value.trim() })} />
            </Field>
            <input
              type="color"
              aria-label={t('appearance.color')}
              value={hexOk ? a.accent_color : '#000000'}
              onChange={(e) => setA({ ...a, accent_color: e.target.value.toUpperCase() })}
              className="h-9 w-16 cursor-pointer rounded-[var(--lr-radius-sm)] border border-border-strong bg-surface"
            />
            {check ? (
              <div aria-live="polite" className="flex flex-col gap-2">
                <p className="flex flex-col text-[length:var(--lr-text-sm)] text-fg-secondary">
                  <span>{t('appearance.onWhite', { ratio: formatNumber(check.onWhite, 2) })}</span>
                  <span>{t('appearance.onDark', { ratio: formatNumber(check.onDark, 2) })}</span>
                </p>
                {check.ok ? (
                  <p className="flex items-center gap-1 text-[length:var(--lr-text-sm)] font-medium text-success">
                    <CheckCircle2 className="size-4" aria-hidden="true" />
                    {t('appearance.passes')}
                  </p>
                ) : (
                  <Alert tone="danger" title={t('appearance.fails')}>
                    {serverProblems?.length ? (
                      <ul className="list-disc pl-4">
                        {serverProblems.map((p) => (
                          <li key={p}>{p}</li>
                        ))}
                      </ul>
                    ) : null}
                  </Alert>
                )}
              </div>
            ) : null}
          </div>
          <Field id="tom-de-voz" label={`${t('appearance.voice')} (${t('common.optional')})`} hint={t('appearance.voiceHelp')}>
            <Textarea rows={4} value={a.voice ?? ''} maxLength={1500} onChange={(e) => setA({ ...a, voice: e.target.value })} placeholder={t('appearance.voicePlaceholder')} />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <ImageField id="logo" label={t('appearance.logo')} value={a.logo} onChange={(logo) => setA({ ...a, logo })} />
            <ImageField id="icone" label={t('appearance.icon')} value={a.icon} onChange={(icon) => setA({ ...a, icon })} />
          </div>
          <div>
            <Button variant="primary" disabled={!ok || pending} onClick={() => save({ ...settings, appearance: { ...a, assistant_name: a.assistant_name.trim() } })}>
              {t('common.save')}
            </Button>
          </div>
        </CardContent>
      </Card>
      <Preview name={a.assistant_name} accent={check?.ok ? a.accent_color : settings.appearance.accent_color} icon={a.icon} />
    </div>
  );
}

export function AppearancePage() {
  const { t } = useTranslation();
  const q = useApiQuery('settings');
  return (
    <AdminOnly>
      <PageHeader title={t('appearance.title')} subtitle={t('appearance.subtitle')} />
      <QueryState query={q}>{(s) => <AppearanceForm settings={s} />}</QueryState>
    </AdminOnly>
  );
}
